<template>
  <div v-redirect-if-no-permission:AUTHENTICATION_LOG>
    <loading-component :value="loading" />

    <v-layout row wrap align-center class="mb-3 ml-3 mt-3 mr-3">
      <v-flex xs12 sm6 md4>
        <v-autocomplete
          v-model="userId"
          :items="userOptions"
          :loading="userLoading"
          :search-input.sync="userSearch"
          :label="$t('monitoring.authLogs.user')"
          :placeholder="$t('monitoring.authLogs.startTypingToSearch')"
          item-value="id"
          item-text="text"
          clearable
          hide-details
          hide-no-data
          cache-items
          @input="onUserSelected"
        >
          <template slot="item" slot-scope="data">
            <v-list-tile-content>
              <v-list-tile-title
                v-html="(data.item.first_name || '') + ' ' + (data.item.last_name || '')"
              ></v-list-tile-title>
              <v-list-tile-sub-title v-html="data.item.username"></v-list-tile-sub-title>
            </v-list-tile-content>
          </template>
        </v-autocomplete>
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="compareDialog = true">
        <v-icon left>compare_arrows</v-icon>
        {{ $t("monitoring.authLogs.compareSchedule") }}
      </v-btn>
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="items"
      :pagination.sync="pagination"
      :total-items="totalItems"
      :rows-per-page-items="[5, 10, 15, 25, 100]"
      :loading="loading"
    >
      <template v-slot:items="props">
        <tr>
          <td class="text-xs-center">{{ props.item.id }}</td>
          <td class="text-xs-center">{{ props.item.full_name }}</td>
          <td class="text-xs-center">{{ props.item.username }}</td>
          <td class="text-xs-center">{{ formatDate(props.item.login_datetime) }}</td>
          <td class="text-xs-center">{{ formatDate(props.item.logout_datetime) }}</td>
          <td class="text-xs-center">{{ formatDate(props.item.last_action_datetime) }}</td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog v-model="compareDialog" persistent width="450">
      <v-card>
        <v-card-title class="headline">{{ $t("monitoring.authLogs.compareSchedule") }}</v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12>
              <v-select
                :items="yearOptions"
                v-model="compareForm.year"
                :label="$t('monitoring.authLogs.year')"
                required
              ></v-select>
            </v-flex>
            <v-flex xs12>
              <v-select
                :items="monthOptions"
                v-model="compareForm.month"
                :label="$t('monitoring.authLogs.month')"
                required
              ></v-select>
            </v-flex>
            <v-flex xs12>
              <v-text-field
                :label="$t('monitoring.authLogs.excelFile')"
                @click="$refs.compareFile.click()"
                v-model="compareForm.fileName"
                append-icon="attach_file"
                readonly
              ></v-text-field>
              <input
                type="file"
                style="display: none"
                ref="compareFile"
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                @change="onCompareFilePicked"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeCompareDialog" :disabled="submitting">{{ $t("common.cancel") }}</v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!isCompareFormValid"
            @click="submitCompare"
          >
            {{ $t("common.submit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import LoadingComponent from "@/components/LoadingComponent";
import { saveAs } from "file-saver";

export default {
  name: "AuthLogs",
  title() {
    return this.$t("monitoring.authLogs.title");
  },
  components: {
    LoadingComponent,
  },
  data() {
    return {
      loading: false,
      items: [],
      totalItems: 0,
      userId: null,
      userSearch: null,
      userOptions: [],
      userLoading: false,
      userSearchTimeout: null,
      pagination: {
        sortBy: "id",
        descending: true,
        rowsPerPage: 25,
        page: 1,
      },
      compareDialog: false,
      submitting: false,
      compareForm: {
        year: null,
        month: null,
        file: null,
        fileName: null,
      },
    };
  },
  computed: {
    headers() {
      return [
        { text: "#", value: "id", sortable: true, align: "center" },
        { text: this.$t("common.name"), value: "full_name", sortable: false, align: "center" },
        { text: this.$t("monitoring.authLogs.email"), value: "username", sortable: false, align: "center" },
        { text: this.$t("monitoring.authLogs.loginTime"), value: "login_datetime", sortable: true, align: "center" },
        { text: this.$t("monitoring.authLogs.logoutTime"), value: "logout_datetime", sortable: true, align: "center" },
        { text: this.$t("monitoring.authLogs.lastActionTime"), value: "last_action_datetime", sortable: true, align: "center" },
      ];
    },
    yearOptions() {
      const current = moment().year();
      const years = [];
      for (let y = current + 1; y >= current - 5; y--) years.push(y);
      return years;
    },
    monthOptions() {
      return [
        { text: this.$t("monitoring.authLogs.months.january"), value: 1 },
        { text: this.$t("monitoring.authLogs.months.february"), value: 2 },
        { text: this.$t("monitoring.authLogs.months.march"), value: 3 },
        { text: this.$t("monitoring.authLogs.months.april"), value: 4 },
        { text: this.$t("monitoring.authLogs.months.may"), value: 5 },
        { text: this.$t("monitoring.authLogs.months.june"), value: 6 },
        { text: this.$t("monitoring.authLogs.months.july"), value: 7 },
        { text: this.$t("monitoring.authLogs.months.august"), value: 8 },
        { text: this.$t("monitoring.authLogs.months.september"), value: 9 },
        { text: this.$t("monitoring.authLogs.months.october"), value: 10 },
        { text: this.$t("monitoring.authLogs.months.november"), value: 11 },
        { text: this.$t("monitoring.authLogs.months.december"), value: 12 },
      ];
    },
    isCompareFormValid() {
      return (
        this.compareForm.year &&
        this.compareForm.month &&
        this.compareForm.file
      );
    },
  },
  watch: {
    pagination: {
      handler() {
        if (this._fetchScheduled) return;
        this._fetchScheduled = true;
        this.$nextTick(() => {
          this._fetchScheduled = false;
          this.getData();
        });
      },
      deep: true,
    },
    userSearch(val) {
      if (this.userSearchTimeout) clearTimeout(this.userSearchTimeout);
      this.userSearchTimeout = setTimeout(() => {
        this.fetchUsers(val);
      }, 400);
    },
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format(" DD-MM-YYYY HH:mm") : "";
    },
    getData() {
      this.loading = true;
      const params = new URLSearchParams();
      params.append("perPage", String(this.pagination.rowsPerPage));
      params.append("page", this.pagination.page);
      if (this.pagination.sortBy) {
        params.append("sortBy", this.pagination.sortBy);
        params.append("descending", String(this.pagination.descending));
      }
      if (this.userId) params.append("user_id", this.userId);
      this.axios
        .get("user_authentication_log", { params })
        .then((res) => {
          const payload = res.data.data || res.data;         

          this.items = payload.data || [];
          this.pagination.rowsPerPage = Number(payload.per_page) || this.pagination.rowsPerPage;
          this.totalItems = payload.total || 0;
          this.loading = false;
        })
        .catch((err) => {
          console.log(err.message);
          this.loading = false;
        });
    },
    onUserSelected() {
      this.pagination.page = 1;
      this.getData();
    },
    fetchUsers(query) {
      this.userLoading = true;
      const params = new URLSearchParams();
      if (query) params.append("search", query);
      this.axios
        .get(this.api.ACTIONS.USERS, { params })
        .then((res) => {
          const payload = (res.data.data && res.data.data.data) || res.data.data || [];
          this.userOptions = payload.map((u) => ({
            ...u,
            text: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
          }));
        })
        .catch((err) => console.log(err.message))
        .finally(() => {
          this.userLoading = false;
        });
    },
    onCompareFilePicked(e) {
      const files = e.target.files;
      if (files && files[0]) {
        this.compareForm.file = files[0];
        this.compareForm.fileName = files[0].name;
      } else {
        this.compareForm.file = null;
        this.compareForm.fileName = null;
      }
    },
    resetCompareForm() {
      this.compareForm.year = null;
      this.compareForm.month = null;
      this.compareForm.file = null;
      this.compareForm.fileName = null;
      if (this.$refs.compareFile) this.$refs.compareFile.value = null;
    },
    closeCompareDialog() {
      this.compareDialog = false;
      this.resetCompareForm();
    },
    submitCompare() {
      if (!this.isCompareFormValid) return;
      const formData = new FormData();
      formData.append("year", this.compareForm.year);
      formData.append("month", this.compareForm.month);
      formData.append("file", this.compareForm.file);

      this.submitting = true;
      this.axios
        .post(this.api.ACTIONS.USER_AUTH_COMPARE_SCHEDULE, formData, {
          responseType: "blob",
        })
        .then((res) => {
          const contentType =
            res.headers["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          const blob = new Blob([res.data], { type: contentType });
          const fileName = `auth_compare_${this.compareForm.year}_${this.compareForm.month}.xlsx`;
          saveAs(blob, fileName);
          window.getApp.$emit("SHOW_TOAST", this.$t("monitoring.authLogs.fileDownloaded"), "green");
          this.closeCompareDialog();
        })
        .catch((err) => {
          const message =
            (err.response && err.response.data && err.response.data.message) ||
            this.$t("monitoring.authLogs.failedToCompareSchedule");
          window.getApp.$emit("SHOW_TOAST", message, "red");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
